import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiListSensors } from "apis/SensorApi";
import { apiListCameraTrafficVolumes } from "apis/CameraTrafficVolume";
import FanLoadingIcon from "components/FanLoadingIcon";
import Icon, { IconList } from "components/Icon";
import "./TrafficPage.css";
import TimeUnitSelector from "./components/TimeUnitSelector";
import { TIME_FRAME } from "utils/constanst";
import Pagination from "components/Pagination";

const getStartTime = (unit) => {
  const now = new Date();
  switch (unit) {
    case TIME_FRAME.TEN_MINUTES:
      now.setMinutes(now.getMinutes() - 10);
      break;
    case TIME_FRAME.DAY:
      now.setDate(now.getDate() - 1);
      now.setHours(0, 0, 0, 0);
      break;
    case TIME_FRAME.WEEK:
      now.setDate(now.getDate() - 7);
      now.setHours(0, 0, 0, 0);
      break;
    case TIME_FRAME.MONTH:
      now.setMonth(now.getMonth() - 1);
      now.setHours(0, 0, 0, 0);
      break;
    case TIME_FRAME.YEAR:
      now.setFullYear(now.getFullYear() - 1);
      now.setHours(0, 0, 0, 0);
      break;
    default:
      now.setDate(now.getDate() - 1);
      now.setHours(0, 0, 0, 0);
  }
  return now.toISOString();
};

const formatCoordinates = (lat, lng) => {
  if (lat == null || lng == null) return "-";
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

function TrafficPage() {
  const [timeUnit, setTimeUnit] = useState(TIME_FRAME.DAY);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "rank", direction: "ascending" });
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  const navigate = useNavigate();

  const handleTimeUnitChange = (e) => {
    setTimeUnit(e.target.value);
    setCurrentPage(1);
  };

  const handleViewTypeChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "設置箇所一覧") {
      navigate("/location");
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setTableData([]);
    setCurrentPage(1);
    const endTimeQuery = new Date().toISOString();
    const startTimeQuery = getStartTime(timeUnit);
    try {
      const sensorsResult = await apiListSensors();
      if (!sensorsResult.success || !sensorsResult.data || !sensorsResult.data.items) {
        throw new Error("Failed to fetch sensors or no sensors found.");
      }
      const sensorsData = sensorsResult.data.items;
      const sensorsToFetchTraffic = sensorsData.filter((sensor) => sensor.data?.trafficVolumeFeature === true);
      if (sensorsToFetchTraffic.length === 0) {
        setLoading(false);
        return;
      }
      const trafficApiPromises = sensorsToFetchTraffic.map((sensor) =>
        apiListCameraTrafficVolumes(sensor.id, startTimeQuery, endTimeQuery)
          .then((result) => ({ sensorId: sensor.id, result }))
          .catch((err) => ({ sensorId: sensor.id, error: err }))
      );
      const trafficResponses = await Promise.all(trafficApiPromises);
      const combinedData = sensorsToFetchTraffic.map((sensor) => {
        let totalCars = 0;
        let totalPedestrians = 0;
        let scenarioCount = 0;
        if (sensor.data) {
          for (let i = 1; i <= 10; i++) {
            if (sensor.data[`trafficVolumeType${i}`]) {
              scenarioCount++;
            }
          }
        }
        const trafficResponse = trafficResponses.find((res) => res.sensorId === sensor.id);
        if (trafficResponse && !trafficResponse.error && trafficResponse.result.success && trafficResponse.result.data) {
          const trafficRecords = trafficResponse.result.data;
          trafficRecords.forEach((record) => {
            try {
              const parsedData = JSON.parse(record.data);
              for (let i = 1; i <= 10; i++) {
                const scenarioType = sensor.data?.[`trafficVolumeType${i}`];

                if (scenarioType === "VEHICLE") {
                  totalCars += parsedData[`count_${i}_car`] || 0;
                  totalCars += parsedData[`count_${i}_truck`] || 0;
                  totalCars += parsedData[`count_${i}_bus`] || 0;
                  totalCars += parsedData[`count_${i}_otherVehicle`] || 0;
                } else if (scenarioType === "HUMAN") {
                  totalPedestrians += parsedData[`count_${i}`] || 0;
                }
              }
            } catch (parseError) {
              console.error(`Error parsing traffic data for record ${record.timestamp} of sensor ${sensor.id}:`, parseError);
            }
          });
        } else if (trafficResponse && trafficResponse.error) {
          console.error(`Error fetching traffic data for sensor ${sensor.id}:`, trafficResponse.error);
        } else {
          console.warn(`No successful traffic data found for sensor ${sensor.id}`);
        }
        const totalTraffic = totalCars + totalPedestrians;
        return {
          id: sensor.id,
          rank: 0,
          totalTraffic: totalTraffic,
          totalCars: totalCars,
          totalPedestrians: totalPedestrians,
          scenarioCount: scenarioCount,
          managementId: sensor.managementId || "-",
          managementArea: sensor.data.suburb || "-",
          displayName: sensor.name || "-",
          address: sensor.location || "-",
          coordinates: formatCoordinates(sensor.lat, sensor.lng),
        };
      });
      combinedData.sort((a, b) => b.totalTraffic - a.totalTraffic);
      const rankedData = combinedData.map((item, index) => ({
        ...item,
        rank: index + 1,
      }));
      setTableData(rankedData);
    } catch (err) {
      console.error("Error fetching or processing data:", err);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [timeUnit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const sortedTableData = useMemo(() => {
    let sortableItems = [...tableData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (typeof valA === "number" && typeof valB === "number") {
          if (valA < valB) return sortConfig.direction === "ascending" ? -1 : 1;
          if (valA > valB) return sortConfig.direction === "ascending" ? 1 : -1;
        } else {
          const strA = String(valA);
          const strB = String(valB);
          if (strA < strB) return sortConfig.direction === "ascending" ? -1 : 1;
          if (strA > strB) return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [tableData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedTableData.slice(startIndex, endIndex);
  }, [sortedTableData, currentPage]);

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "ascending" ? " ▲" : " ▼";
  };

  return (
    <div className="traffic-page-container">
      <div className="traffic-list_header">
        <Icon icon={IconList.iconListing} />
        <label className="traffic-header-text">交通量一覧</label>
      </div>
      <div className="traffic-selectors-container">
        <select className="traffic-view-type-selector" value="交通量一覧" onChange={handleViewTypeChange}>
          <option value="交通量一覧">交通量一覧</option>
          <option value="設置箇所一覧">設置箇所一覧</option>
        </select>
        <TimeUnitSelector timeUnit={timeUnit} onChange={handleTimeUnitChange} />
        <Pagination total={sortedTableData.length} pageNum={Math.ceil(sortedTableData.length / ITEMS_PER_PAGE)} curPage={currentPage} onPageChange={(page) => setCurrentPage(page)} itemNumPerPage={ITEMS_PER_PAGE} isNew={true} />
      </div>

      {loading && <FanLoadingIcon />}
      {error && <div className="traffic-status-message traffic-error-message">エラー: {error}</div>}
      {!loading && !error && tableData.length === 0 && <div className="traffic-status-message">表示するデータがありません。</div>}
      {!loading && !error && tableData.length > 0 && (
        <>
          <div className="traffic-table-wrapper">
            <table className="traffic-table">
              <thead className="traffic-table-header">
                <tr>
                  <th scope="col" className="traffic-table-th" onClick={() => requestSort("rank")}>
                    順位{getSortDirectionIndicator("rank")}
                  </th>
                  <th scope="col" className="traffic-table-th" onClick={() => requestSort("totalTraffic")}>
                    交通量{getSortDirectionIndicator("totalTraffic")}
                  </th>
                  <th scope="col" className="traffic-table-th">
                    自動車合計
                  </th>
                  <th scope="col" className="traffic-table-th">
                    歩行者合計
                  </th>
                  <th scope="col" className="traffic-table-th">
                    シナリオ数
                  </th>
                  <th scope="col" className="traffic-table-th">
                    管理番号
                  </th>
                  <th scope="col" className="traffic-table-th">
                    管理エリア
                  </th>
                  <th scope="col" className="traffic-table-th">
                    表示名
                  </th>
                  <th scope="col" className="traffic-table-th">
                    住所
                  </th>
                  <th scope="col" className="traffic-table-th">
                    座標
                  </th>
                </tr>
              </thead>
              <tbody className="traffic-table-body">
                {paginatedData.map((item) => (
                  <tr key={item.id} className="traffic-table-row">
                    <td className="traffic-table-td traffic-table-td-rank">{item.rank}</td>
                    <td className="traffic-table-td traffic-text-right">{item.totalTraffic.toLocaleString()}</td>
                    <td className="traffic-table-td traffic-text-right">{item.totalCars.toLocaleString()}</td>
                    <td className="traffic-table-td traffic-text-right">{item.totalPedestrians.toLocaleString()}</td>
                    <td className="traffic-table-td traffic-text-center">{item.scenarioCount}</td>
                    <td className="traffic-table-td traffic-table-td-link">
                      <a
                        href={`/base/${item.id}`}
                        onClick={(e) => {
                        }}
                      >
                        {item.managementId}
                      </a>
                    </td>
                    <td className="traffic-table-td">{item.managementArea}</td>
                    <td className="traffic-table-td">{item.displayName}</td>
                    <td className="traffic-table-td">{item.address}</td>
                    <td className="traffic-table-td">{item.coordinates}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
export default TrafficPage;
